
import { defineComponent, ref, getCurrentInstance, SetupContext, computed, PropType, watch } from 'vue';
import Toast from 'vant/lib/toast';
import Cookies from 'js-cookie';

import { formateNum } from '@/common/utils';
import { AIFeed, AIFeedLiked, AIFeedApiService } from '@/api/ai-feed';
import { sendLog } from '@/common/radar';
import { getLlsidFromExptag } from '@/common/common-params';

import { AIFeedPageEnum } from '@/store/modules/ai-feed';
import { UserInfoByLogin } from '@/api/user';
import { AIActionType, AIActionItem, AIIconMap, AIActionLogNameMap } from './scripts/ai_common';

import { AIActionBarAppSetup } from './scripts/ai_app';
import { AIActionBarSeoSetup } from './scripts/ai_seo';
import { logForClickAction, logForClickLikeButton, logForClickReportButton, logForClickShareButton } from '../../scripts/logger';
import { checkIfBomdiaApp, checkIfKwaiProApp } from '@/common/app';

const AIActionBar = defineComponent({
    components: {},

    props: {
        index: {
            type: Number,
            default: 0,
        },
        curIndex: {
            type: Number,
            default: 0,
        },
        aiFeed: {
            type: Object as PropType<AIFeed> | undefined,
            default: undefined,
        },
        feedPage: {
            type: String as PropType<AIFeedPageEnum> | undefined,
            default: AIFeedPageEnum.AIForyou,
        },
    },

    setup(props, ctx: SetupContext<Array<'onChangeLoginPopupVisible'>>) {
        const { proxy } = getCurrentInstance() || {};
        const setupConfig: Partial<ReturnType<typeof AIActionBarAppSetup> & ReturnType<typeof AIActionBarSeoSetup>> = proxy?.$isAppEnvironment
            ? AIActionBarAppSetup(props, ctx)
            : AIActionBarSeoSetup(props, ctx);

        const isFollowingDuration = ref(false);
        const query = (proxy?.$route?.query || {}) as { from_page?: string };
        const isFromProfile = query?.from_page === 'profile';
        const extraActionListPopupVisible = ref(false);

        const showNotificationPopup = () => {
            proxy?.$store?.commit?.('setShowNotificationPopup', true);
        };

        const feedList = computed<AIFeed[]>(() => {
            const { feedListOrigin } = proxy?.$store?.state?.aiFeedMoudle || {};
            return feedListOrigin?.[props?.feedPage] || [];
        });

        const feed = computed(() => {
            return props.aiFeed;
        });

        // const feedLikeStatus = computed(() => {
        //     return feed.value?.liked === AIFeedLiked.Like;
        // });
        const feedLikeStatus = ref(setupConfig.likeStatus?.value === AIFeedLiked.UnLike);
        const chatMode = ref(true);
        const userInfoByLogin = computed<UserInfoByLogin>(() => {
            const userInfoByLogin = (proxy?.$store?.state?.userModule?.userInfo || {}) as UserInfoByLogin;
            return userInfoByLogin;
        });

        const sendActionLog = (type: AIActionType) => {
            const logActionName = AIActionLogNameMap[type];
            if (!logActionName) {
                return;
            }

            const { user, userId, user_id } = userInfoByLogin.value || {};
            const uid = userId || user_id || user?.user_id || Cookies.get('userId');

            sendLog({
                type: 'click',
                name: logActionName,
                value: {
                    user_id: uid,
                    kwai_id: user?.kwai_id,
                    author_id: feed?.value?.feed?.user_id_str || feed?.value?.feed?.user_id,
                    author_kwai_id: feed?.value?.feed?.kwai_id,
                    bot_id: feed?.value?.bot_id_str,
                    llsid: getLlsidFromExptag(feed?.value?.feed?.exp_tag),
                    type: 'BOT',
                    exp_tag: feed?.value?.feed?.exp_tag,
                    identity: feed?.value?.bot_id_str,
                    index: props?.curIndex,
                },
            });
        };

        const actionList = computed(() => {
            const newFeed = feed;
            const actionList: Array<AIActionItem> = [
                {
                    type: AIActionType.Like,
                    icon: AIIconMap[AIActionType.Like],
                    count: setupConfig.likeCount?.(newFeed.value?.feed?.like_count || 0),
                    hidden: feedLikeStatus.value || props?.aiFeed?.compliance_mark_show,
                    onClick: () => {
                        logForClickLikeButton(props.aiFeed, props.index + 1, props.feedPage);
                        // showNotificationPopup();
                        feedLikeStatus.value = !feedLikeStatus.value;
                        sendActionLog(AIActionType.Like);
                        setupConfig.handleLikeAction(AIActionType.Like);
                        logForClickAction(AIActionType.Like, newFeed.value);
                    },
                },
                {
                    type: AIActionType.UnLike,
                    icon: AIIconMap[AIActionType.UnLike],
                    count: setupConfig.likeCount?.(newFeed.value?.feed?.like_count || 0),
                    hidden: !feedLikeStatus.value,
                    onClick: () => {
                        // showNotificationPopup();
                        feedLikeStatus.value = !feedLikeStatus.value;
                        setupConfig.handleLikeAction(AIActionType.UnLike);
                        logForClickAction(AIActionType.UnLike, newFeed.value);
                    },
                },
                {
                    type: AIActionType.Share,
                    icon: AIIconMap[AIActionType.Share],
                    count: setupConfig.shareCount?.(newFeed.value?.feed?.forward_count || 0),
                    hidden: !setupConfig.isShareButtonEnabled?.value || props?.aiFeed?.compliance_mark_show,
                    onClick: () => {
                        logForClickShareButton(props.aiFeed, props.index + 1, props.feedPage);
                        // showNotificationPopup();
                        sendActionLog(AIActionType.Share);
                        logForClickAction(AIActionType.Share, newFeed.value);
                        if (!props.aiFeed) {
                            return;
                        }
                        const searchParams = new URLSearchParams(window?.location?.search);
                        searchParams?.set('pwa_source', 'aishare');
                        const newSearch = searchParams?.toString();
                        const copyUrl = `${window?.location?.origin}/aichat/bot/${props?.aiFeed?.bot_id_str || ''}?${newSearch}`;
                        setupConfig.handleShareAction(copyUrl);
                        Toast({
                            message: proxy?.$root?.$t?.('k_446636'),
                        });
                    },
                },
                {
                    type: AIActionType.Report,
                    icon: AIIconMap[AIActionType.Report],
                    hidden: checkIfBomdiaApp() || checkIfKwaiProApp() || props?.aiFeed?.is_bot_reported || props?.aiFeed?.compliance_mark_show,
                    onClick: () => {
                        logForClickReportButton(props.aiFeed, props.index + 1, props.feedPage);
                        proxy?.$router?.push?.({
                            path: `/aichat/report`,
                            query: { ...query, feedPage: props.feedPage, botId: props?.aiFeed?.bot_id_str },
                        });
                    },
                },
                // {
                //     type: AIActionType.ChatMode,
                //     icon: AIIconMap[AIActionType.ChatMode],
                //     count: setupConfig.shareCount?.(newFeed.value?.feed?.forward_count || 0),
                //     hidden: !chatMode.value || proxy?.$isAppEnvironment,
                //     onClick: () => {
                //         sendActionLog(AIActionType.ChatMode);
                //         logForClickAction(AIActionType.ChatMode, newFeed.value);
                //         setupConfig.handleSwitchChatModeAction(true);
                //         chatMode.value = !chatMode.value;
                //     },
                // },
                // {
                //     type: AIActionType.HistroyMode,
                //     icon: AIIconMap[AIActionType.HistroyMode],
                //     count: setupConfig.shareCount?.(newFeed.value?.feed?.forward_count || 0),
                //     hidden: chatMode.value || proxy?.$isAppEnvironment,
                //     onClick: () => {
                //         // sendActionLog(AIActionType.HistroyMode);
                //         // logForClickAction(AIActionType.ChatMode, newFeed.value);
                //         setupConfig.handleSwitchChatModeAction(false);
                //         chatMode.value = !chatMode.value;
                //     },
                // },
            ];
            // xinqi.chrome-plugin.corp.kuaishou.com/aichat/bot/687810569247670543pwa_source=aichat
            return actionList;
        });

        // 客服机器人没有举报选项
        const finalActionList = computed(() => {
            if (props?.aiFeed?.bot_id_str === '738470468051295297') {
                return actionList.value.filter(item => item.type !== AIActionType.Report);
            }
            return actionList.value;
        });

        watch(
            () => props.curIndex,
            (newVal, oldVal) => {
                if (Math.abs(newVal - props.index) === 1 && chatMode.value === false) {
                    chatMode.value = true;
                    setupConfig.handleSwitchChatModeAction(false);
                }
            },
            {
                immediate: true,
            }
        );

        return {
            formateNum,
            AIIconMap,
            AIActionType,
            actionList,
            finalActionList,
            feed,
            isFollowingDuration,
            isFromProfile,
            extraActionListPopupVisible,
        };
    },
});

export default AIActionBar;
